import type { z } from 'zod';
import type { AnyHint } from './layout';

/**
 * CSV in and out for a `list` field — a template a person can fill in a spreadsheet,
 * and the reading of what they send back. RFC 4180 as far as it goes: quoted cells,
 * doubled quotes inside them, commas and line breaks inside quotes, CR, LF or CRLF
 * line endings, and the byte-order mark Excel writes. No library; the whole thing is a
 * page.
 *
 * Nothing here touches the network or the file system. The browser reads the file and
 * hands the text over; only the rows that survive reach the form.
 */

/** Rows of cells. An empty line is skipped; a line of empty cells is kept (it may be a row). */
export function parseCsv(text: string): string[][] {
  const input = text.startsWith('﻿') ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  let i = 0;

  const endRow = () => {
    row.push(cell);
    cell = '';
    if (row.some((value) => value !== '')) rows.push(row);
    row = [];
  };

  while (i < input.length) {
    const ch = input[i]!;
    if (quoted) {
      if (ch === '"') {
        if (input[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\r') {
      if (input[i + 1] === '\n') i += 1;
      endRow();
    } else if (ch === '\n') {
      endRow();
    } else {
      cell += ch;
    }
    i += 1;
  }
  if (cell !== '' || row.length > 0) endRow();
  return rows;
}

function quote(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/** Rows as CSV text with CRLF endings, which every spreadsheet opens cleanly. */
export function serializeCsv(rows: readonly (readonly string[])[]): string {
  return rows.map((row) => row.map(quote).join(',')).join('\r\n') + '\r\n';
}

/** The header row a list field's template starts with: its column labels. */
export function csvHeader(hint: AnyHint): string[] {
  return (hint.columns ?? []).map((column) => column.label);
}

/** A template for a list field: the header row and nothing else, ready to fill in. */
export function csvTemplate(hint: AnyHint): string {
  return serializeCsv([csvHeader(hint)]);
}

/** Header matching is forgiving: "First Name", "first_name" and "firstName" all match. */
function fold(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** What a checkbox column accepts in a spreadsheet cell. */
function parseBoolean(cell: string): boolean | undefined {
  const value = fold(cell);
  if (value === '' || value === 'no' || value === 'false' || value === '0' || value === 'n') {
    return false;
  }
  if (value === 'yes' || value === 'true' || value === '1' || value === 'y' || value === 'x') {
    return true;
  }
  return undefined;
}

export interface CsvIssue {
  /** 1-based line in the file, as a spreadsheet shows it. */
  line: number;
  message: string;
}

export interface ParsedListCsv {
  /**
   * Every data row, as the form's *input* values: strings, and booleans for checkboxes.
   * Rows with problems are included too, so the person can fix a small mistake in the
   * form rather than in the file; `issues` says which lines those are.
   */
  rows: Record<string, unknown>[];
  issues: CsvIssue[];
}

/**
 * Read a filled-in template back into rows for a list field.
 *
 * Columns are matched by header, forgivingly, against each column's label and its key,
 * so a template edited in Excel and one written by hand both work. A column the file
 * does not have is an issue on line 1 and nothing is imported, since every row would be
 * wrong the same way. Each data row is checked with the row schema; a row with a problem
 * is still returned — the form will show the problem on the cell — and reported by line.
 */
export function parseListCsv(hint: AnyHint, rowSchema: z.ZodType, text: string): ParsedListCsv {
  const columns = hint.columns ?? [];
  const lines = parseCsv(text);
  const header = lines[0];
  if (!header) return { rows: [], issues: [{ line: 1, message: 'The file is empty.' }] };

  const folded = header.map(fold);
  const positions = new Map<string, number>();
  for (const column of columns) {
    const index = folded.findIndex(
      (cell) => cell === fold(column.label) || cell === fold(column.name),
    );
    if (index >= 0) positions.set(column.name, index);
  }
  const missing = columns.filter((column) => !positions.has(column.name));
  if (missing.length > 0) {
    return {
      rows: [],
      issues: [
        {
          line: 1,
          message: `Missing column${missing.length > 1 ? 's' : ''}: ${missing
            .map((column) => column.label)
            .join(', ')}. Expected: ${csvHeader(hint).join(', ')}.`,
        },
      ],
    };
  }

  const rows: Record<string, unknown>[] = [];
  const issues: CsvIssue[] = [];
  lines.slice(1).forEach((cells, offset) => {
    const line = offset + 2;
    const row: Record<string, unknown> = {};
    for (const column of columns) {
      const cell = (cells[positions.get(column.name)!] ?? '').trim();
      if (column.widget === 'checkbox') {
        const flag = parseBoolean(cell);
        if (flag === undefined) {
          // Imported as unticked; the person decides, told which line it was.
          issues.push({
            line,
            message: `${column.label}: use yes or no, not "${cell}" (left unticked)`,
          });
        }
        row[column.name] = flag ?? false;
      } else {
        row[column.name] = cell;
      }
    }
    const result = rowSchema.safeParse(row);
    if (!result.success) {
      const first = result.error.issues[0];
      const column = columns.find((c) => c.name === first?.path[0]);
      issues.push({
        line,
        message: `${column?.label ?? String(first?.path[0] ?? 'row')}: ${first?.message ?? 'invalid'}`,
      });
    }
    rows.push(row);
  });

  return { rows, issues };
}
